/**
 * Client model for sidebar chat history API.
 * Persists chats to backend for cross-device sync.
 */
const GlobalVariables = require("./globalVariables");

const BASE_PATH = "/api/sidebar-chat-history";
const HEADERS = { "Content-Type": "application/json" };

// A chat is saved on every streamed chunk. Parallel saves can land out of
// order and leave the server with a half-written answer (no citations), so
// each chat saves one request at a time and only its newest version is sent
// next. Keyed by chat ID: { pendingPayload, done }.
const chatSaveQueues = new Map();

const postChatSave = async (payload) => {
  try {
    const res = await fetch(`${SidebarChatHistoryModel.getBaseURL()}/save`, {
      method: "POST",
      headers: HEADERS,
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    return data.success ? { success: true } : null;
  } catch (err) {
    console.warn("[SidebarChatHistory] save failed:", err);
    return null;
  }
};

const enqueueChatSave = (payload) => {
  const { chatId } = payload;
  const queue = chatSaveQueues.get(chatId);
  if (queue) {
    queue.pendingPayload = payload;

    return queue.done;
  }

  const newQueue = { pendingPayload: payload, done: null };
  newQueue.done = (async () => {
    let result = null;
    while (newQueue.pendingPayload) {
      const nextPayload = newQueue.pendingPayload;
      newQueue.pendingPayload = null;
      result = await postChatSave(nextPayload);
    }
    chatSaveQueues.delete(chatId);

    return result;
  })();
  chatSaveQueues.set(chatId, newQueue);

  return newQueue.done;
};

class SidebarChatHistoryModel {
  static getBaseURL() {
    return GlobalVariables.getAPIURL() + BASE_PATH;
  }

  /**
   * Save or update a chat in the backend.
   * Fire-and-forget; does not throw. Caller should not block on this.
   * @param {string} uid - Firebase UID
   * @param {Object} chatData - Full chat object (chatID -> chatId for backend)
   * @returns {Promise<{success: boolean}|null>} Resolves to {success: true} or null on failure
   */
  static async saveChat(uid, chatData) {
    if (!uid || !chatData) return null;

    const payload = {
      chatId: chatData.chatID,
      uid,
      messages: chatData.messages || [],
      chatName: chatData.chatName || "Chat",
      appID: chatData.appID || "",
      publicId: chatData.publicId || "",
      clientHostname: chatData.clientHostname || "",
      loanId: chatData.loanId || null,
      organizationId: chatData.organizationId || null,
      dateStarted: chatData.dateStarted || Date.now(),
      lastActive: chatData.lastActive || Date.now(),
    };

    return enqueueChatSave(payload);
  }

  /**
   * Get a single chat from the backend.
   * @param {string} uid - Firebase UID
   * @param {string} chatId - Chat UUID
   * @returns {Promise<Object|null>} Chat data or null if not found
   */
  static async getChat(uid, chatId) {
    if (!uid || !chatId) return null;

    try {
      const res = await fetch(
        `${SidebarChatHistoryModel.getBaseURL()}/get?uid=${encodeURIComponent(uid)}&chatId=${encodeURIComponent(chatId)}`,
        { headers: HEADERS }
      );
      const data = await res.json();
      if (data.success && data.chat) {
        return data.chat;
      }
      return null;
    } catch (err) {
      console.warn("[SidebarChatHistory] get failed:", err);
      return null;
    }
  }

  /**
   * List all chats for a user from the backend.
   * @param {string} uid - Firebase UID
   * @returns {Promise<Array>} Array of chat metadata
   */
  static async listChats(uid) {
    if (!uid) return [];

    try {
      const res = await fetch(
        `${SidebarChatHistoryModel.getBaseURL()}/list?uid=${encodeURIComponent(uid)}`,
        { headers: HEADERS }
      );
      const data = await res.json();
      if (data.success && Array.isArray(data.chats)) {
        return data.chats;
      }
      return [];
    } catch (err) {
      console.warn("[SidebarChatHistory] list failed:", err);
      return [];
    }
  }

  /**
   * One page of a user's chats, newest first: names and dates, no messages.
   * A backend without paging ignores `limit` and sends every chat with no
   * `nextCursor`, which reads as a single, last page.
   * @param {string} uid - Firebase UID
   * @param {{limit: number, cursor?: string|null}} options - cursor is the previous page's nextCursor
   * @returns {Promise<{chats: Array, nextCursor: string|null}>} nextCursor is null on the last page
   */
  static async listChatsPage(uid, { limit, cursor } = {}) {
    if (!uid) return { chats: [], nextCursor: null };

    // No object spread in this file: the production build would add a helper `import`, which
    // makes webpack treat it as an ES module and drop its `module.exports` default.
    const params = new URLSearchParams({ uid, limit: String(limit) });
    if (cursor) {
      params.set("cursor", cursor);
    }
    try {
      const res = await fetch(`${SidebarChatHistoryModel.getBaseURL()}/list?${params}`, { headers: HEADERS });
      const data = await res.json();
      if (data.success && Array.isArray(data.chats)) {
        return { chats: data.chats, nextCursor: data.nextCursor || null };
      }
      return { chats: [], nextCursor: null };
    } catch (err) {
      console.warn("[SidebarChatHistory] list page failed:", err);
      return { chats: [], nextCursor: null };
    }
  }

  /**
   * Get lightweight loan info (borrower names + loan number) for a loan-associated chat.
   * @param {string} uid - Firebase UID
   * @param {string} loanId - Loan document ID
   * @param {string} organizationId - Organization ID that owns the loan
   * @returns {Promise<{borrowers: Array<{firstName:string,lastName:string}>, loanNumber: string|null}|null>}
   */
  static async getLoanInfo(uid, loanId, organizationId) {
    if (!uid || !loanId || !organizationId) return null;

    try {
      const res = await fetch(
        `${SidebarChatHistoryModel.getBaseURL()}/loan-info?uid=${encodeURIComponent(uid)}&loanId=${encodeURIComponent(loanId)}&organizationId=${encodeURIComponent(organizationId)}`,
        { headers: HEADERS }
      );
      const data = await res.json();
      if (data.success) {
        return { borrowers: data.borrowers || [], loanNumber: data.loanNumber || null };
      }
      return null;
    } catch (err) {
      console.warn("[SidebarChatHistory] getLoanInfo failed:", err);
      return null;
    }
  }

  /**
   * Rename a chat in the backend.
   * @param {string} uid - Firebase UID
   * @param {string} chatId - Chat UUID
   * @param {string} newName - New display name
   * @returns {Promise<boolean>} True if successful
   */
  static async renameChat(uid, chatId, newName) {
    if (!uid || !chatId || !newName) return false;

    try {
      const res = await fetch(`${SidebarChatHistoryModel.getBaseURL()}/rename`, {
        method: "POST",
        headers: HEADERS,
        body: JSON.stringify({ uid, chatId, newName: newName.trim() }),
      });
      const data = await res.json();
      return !!data.success;
    } catch (err) {
      console.warn("[SidebarChatHistory] rename failed:", err);
      return false;
    }
  }
}

module.exports = SidebarChatHistoryModel;
